import { apiErrorMessage } from '@agos/api-client'
import type { OrderListItem } from '@agos/api-client/model'
import {
  getGetOrdersQueryKey,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  useGetSalesChannels,
} from '@agos/api-client/orders'
import {
  chimeToggleLabel,
  credentialsNeeded,
  filterChannelsOf,
  formatMoney,
  startOfDay,
  T,
  tt,
  useArrivalChime,
  useAuth,
} from '@agos/shared'
import {
  CancelOrderModal,
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
  EmptyState,
  PageHeader,
  StatusBadge,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  CheckCheck,
  CircleDot,
  Pencil,
  Plus,
  Receipt,
  RefreshCw,
  ShoppingBag,
  Store,
  Users,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearch } from 'wouter'


import { orderStatusColor, orderStatusLabel } from '../order-status'
import { openRoomOrders } from '../table-map/table-status'
import { PaymentDialog, PAYMENT_POLL_MS } from './PaymentDialog'
import {
  needsReceiptStorno,
  noteOf,
  openForNote,
} from './rounds'
import {
  SERVED,
  allServed,
  effectiveServed,
  toggledFrom,
  useServedLines,
  type PendingTick,
  type ServableLine,
} from './served-lines'

/**
 * `/restaurant` — the till, ported from legacy `pages/restaurant.tsx`.
 *
 * The room's open tables, and the two things a waiter does with one: add to it
 * or settle it. Adding goes to the order builder with the table already chosen;
 * settling opens the payment dialog, which can take the whole bill or split it.
 *
 * §14.1 — the board polls every eight seconds, which is what legacy uses while
 * a payment dialog is open. A table cancelled in the kitchen while a waiter is
 * mid-payment has to surface before they take the money.
 */

const REFRESH_MS = PAYMENT_POLL_MS
const ORDERS_PAGE_SIZE = '500'

export function RestaurantPage() {
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  const search = useSearch()

  const [payingId, setPayingId] = useState<number | null>(() => {
    const requested = new URLSearchParams(search).get('pay')
    return requested ? Number(requested) : null
  })
  const [cancellingId, setCancellingId] = useState<number | null>(() => {
    const requested = new URLSearchParams(search).get('cancel')
    return requested ? Number(requested) : null
  })

  const today = startOfDay(new Date()).toISOString()
  const { data: result, isLoading } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: REFRESH_MS } },
  )

  // §9.126 — one clock for every card, advanced on the same cadence the list refreshes on. Reading
  // `Date.now()` inside a card would be an impure call during render and would also let two cards
  // disagree about what time it is.
  // §9.127 — one writer for both this screen and the table-map panel.
  const { ticks, isMarkingAll, setServed, markAllServed } = useServedLines(() => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
  })

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), REFRESH_MS)
    return () => clearInterval(tick)
  }, [])
  const orders = useMemo(() => result?.data ?? [], [result])
  const { user } = useAuth()
  const chime = useArrivalChime()
  const previousTableCount = useRef<number | null>(null)

  /**
   * One card per occupied table. Reuses the floor plan's mapping, so the two
   * screens can never disagree about which order belongs to which table.
   */
  // §9.393 - the counter half of this list asks the channel table what a counter collection is,
  // rather than spelling `bar`. Same hook the dispatch board uses, same cached query.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  const openTables = useMemo(
    () => openRoomOrders(orders, tt('Osobný odber'), channels),
    [orders, channels],
  )

  const refresh = () => void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })

  /**
   * A table the room did not have a moment ago — §9.110.
   *
   * <p>This screen had no arrival tracking of its own; it simply redrew. The event a waiter cares
   * about is a **new table opening**, not an order changing on one they are already serving, so the
   * count of open tables is what is watched. `null` until the first load, so arriving at a room with
   * six tables already open does not announce six.
   *
   * <p><b>Gentle, like the kitchen.</b> `urgent` stays reserved for dispatch's unaccepted web order;
   * a table opening is the normal event of a service.
   */
  useEffect(() => {
    const previous = previousTableCount.current
    if (previous !== null && openTables.length > previous) {
      chime.play('gentle')
    }
    previousTableCount.current = openTables.length
  }, [openTables.length, chime])

  const paying = orders.find((order) => order.id === payingId) ?? null
  const cancelling = orders.find((order) => order.id === cancellingId) ?? null

  const cancelOrder = async (email: string, password: string, reason: string) => {
    if (!cancelling) return
    await postOrdersOrderIdConfirmStatus(cancelling.id ?? 0, {
      newStatus: 'cancelled',
      reason,
      // Its own optimistic-concurrency check, and nothing to do with who is acting.
      expectedStatus: cancelling.status,
      // §9.344 - sent only when the dialog collected them. `OrderActorResolver.resolve` tests for
      // **null**, so a blank pair is refused rather than falling back to the signed-in employee.
      ...(email.trim() === '' ? {} : { email: email.trim(), password }),
    })
    setCancellingId(null)
    refresh()
    toast({
      title: tt('Objednávka zrušená'),
      description: `#${cancelling.orderNumber}`,
    })
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Otvorené stoly')}
        description={tt('Klikni na stôl pre pridanie alebo zaplatenie.')}
        icon={<Store className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={chime.toggle}
              aria-label={chimeToggleLabel(chime.enabled)}
              data-sound-toggle={chime.enabled ? 'on' : 'off'}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              {chime.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              aria-label={tt('Obnoviť')}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/new-order?type=restaurant')}
              className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
            >
              <Plus className="w-3.5 h-3.5" /> <T>Nová objednávka</T>
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3].map((placeholder) => (
            <div
              key={placeholder}
              className="h-40 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : openTables.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Store className="w-10 h-10" />}
            title={tt('Žiadne otvorené stoly')}
            description={tt('Nová objednávka otvorí stôl.')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {openTables.map(({ key, heading, order, isPickup }) => (
            <TableCard
              key={key}
              tableName={heading}
              isPickup={isPickup}
              order={order}
              now={now}
              ticks={ticks}
              isMarkingAll={isMarkingAll}
              onToggleLine={(line, next) => void setServed(order.id ?? 0, line, next)}
              onServeAll={() => void markAllServed(order.id ?? 0, order.items ?? [])}
              // §9.319 - a counter collection has no table to add to, so the link carries the channel
              // and the order alone. Sending `table=Osobný odber` would invent a table of that name.
              onAdd={() =>
                navigate(
                  isPickup
                    ? `/new-order?type=bar&edit=${order.id}`
                    : `/new-order?type=restaurant&table=${encodeURIComponent(heading)}&edit=${order.id}`,
                )
              }
              onPay={() => setPayingId(order.id ?? null)}
              onCancel={() => setCancellingId(order.id ?? null)}
            />
          ))}
        </div>
      )}

      {paying && (
        <PaymentDialog
          order={paying}
          onClose={() => setPayingId(null)}
          onPaid={refresh}
        />
      )}

      {cancelling && (
        <CancelOrderModal
          order={cancelling}
          askCredentials={credentialsNeeded(user?.systemRole ?? '', 'cancelled')}
          // §9.344 - a waiter closing a table is still asked, because `cancelled` is not theirs by
          // role; an administrator is not, because their session already proves it.
          onClose={() => setCancellingId(null)}
          onConfirm={async (email, password, reason) => {
            try {
              await cancelOrder(email, password, reason)
            } catch (caught) {
              throw new Error(apiErrorMessage(caught, tt('Neplatné prihlasovacie údaje')), {
                cause: caught,
              })
            }
          }}
        />
      )}
    </div>
  )
}

function TableCard({
  tableName,
  isPickup,
  order,
  now,
  ticks,
  isMarkingAll,
  onToggleLine,
  onServeAll,
  onAdd,
  onPay,
  onCancel,
}: {
  tableName: string
  /** §9.319 - a counter collection, which names itself rather than a table. */
  isPickup: boolean
  order: OrderListItem
  /** The page's clock, ticked with the list — see `openFor` below. */
  now: number
  /** Ticks in flight, shared with every other card — §9.127. */
  ticks: Record<number, PendingTick>
  isMarkingAll: boolean
  onToggleLine: (line: ServableLine & { quantity?: number; productName?: string }, next: string) => void
  onServeAll: () => void
  onAdd: () => void
  onPay: () => void
  onCancel: () => void
}) {
  const lineCount = order.items?.length ?? 0
  // §9.126 — read once per render from a clock the page ticks, never `Date.now()` inside the JSX:
  // an impure call during render is exactly what the lint rule is there to stop.
  const openFor = openForNote(order.createdAt, now)
  const lines = order.items ?? []
  const everythingServed = allServed(lines, ticks)

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col">
      <div
        className="px-4 py-3 border-b border-border flex items-center gap-2"
        data-open-card={isPickup ? 'pickup' : 'table'}
      >
        {isPickup && <ShoppingBag className="w-4 h-4 shrink-0 text-primary" />}
        <span className="text-lg font-black">{tableName}</span>
        <StatusBadge color={orderStatusColor(order.status)} label={orderStatusLabel(order.status)} />
        <span className="ml-auto text-lg font-black text-primary tabular-nums">
          {formatMoney(order.total)}
        </span>
      </div>

      <div className="px-4 py-2.5 flex-1 space-y-1">
        {/* §9.126 - the number and the time the card never said. The number is how this bill is
          * found on every other screen; the time is how a waiter knows which table has been sitting. */}
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
          <span className="font-mono font-bold text-primary" data-open-order-number>
            #{order.orderNumber}
          </span>
          {openFor !== '' && <span data-open-for>· {openFor}</span>}
          <span>
            · <Users className="w-3 h-3 inline" /> {lineCount} {tt('pol.')}
            {order.employeeName ? ` · ${order.employeeName}` : ''}
          </span>
        </p>
        {/* §9.375, FB-669 - the note. **A missed port**: legacy's open-table card draws it with a
          * 📝 (`restaurant.tsx:354`) and AGOS drew the table, the lines and the total without it, so
          * *bez cibule* reached the kitchen ticket and never the person carrying the plate.
          * `whitespace-pre-line` because the owner writes these on two lines and means two lines. */}
        {noteOf(order) !== '' && (
          <p
            className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1 whitespace-pre-line"
            data-open-order-note
          >
            <span className="shrink-0">📝</span>
            {noteOf(order)}
          </p>
        )}
        {/* §9.127 - the lines, each one strikeable once it has been carried out. This was a joined
          * string clamped to two rows: on a bill of any size most of the order was not even visible,
          * and none of it was clickable. */}
        {lines.length === 0 ? (
          <p className="text-xs text-muted-foreground">-</p>
        ) : (
          <ul className="space-y-px" data-served-lines>
            {lines.map((line) => {
              const state = effectiveServed(line, ticks)
              const isServed = state === SERVED
              return (
                <li key={line.id ?? `${line.productName}`}>
                  <button
                    type="button"
                    data-served-line={line.id}
                    data-served={isServed ? 'yes' : 'no'}
                    onClick={() => void onToggleLine(line, toggledFrom(state))}
                    className={cn(
                      'w-full flex items-center gap-2 px-1.5 py-1 rounded-lg text-left text-xs transition-colors',
                      isServed
                        ? 'text-muted-foreground line-through bg-emerald-500/5 hover:bg-emerald-500/10'
                        : 'hover:bg-muted/50',
                    )}
                  >
                    <span
                      className={cn(
                        'w-4 h-4 rounded-full border flex items-center justify-center shrink-0',
                        isServed
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-border bg-background text-muted-foreground',
                      )}
                    >
                      {isServed ? <CheckCheck className="w-2.5 h-2.5" /> : <CircleDot className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">
                      {line.quantity}× {line.productName}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {lines.length > 0 && !everythingServed && (
          <button
            type="button"
            data-serve-all
            disabled={isMarkingAll}
            onClick={() => void onServeAll()}
            className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-50"
          >
            <T>Vyniesť všetko</T>
          </button>
        )}
        {/* A receipt is printed per payment. Adding to a table that already has
            one means the printed one no longer describes the sale. */}
        {needsReceiptStorno(order) && (
          <p className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
            <T>Doklad už bol vytlačený - zmena vyžaduje storno.</T>
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-px bg-border">
        {/* §9.126 - it opens the whole order for editing, which is what legacy's own
            title says. *Pridať* named one of the things it does and hid the rest. */}
        <CardAction icon={Pencil} label={tt('Upraviť')} onClick={onAdd} />
        <CardAction
          icon={Receipt}
          label={tt('Zaplatiť')}
          onClick={onPay}
          className="text-emerald-600 dark:text-emerald-400"
        />
        <CardAction
          icon={Ban}
          label={tt('Storno')}
          onClick={onCancel}
          className="text-red-500"
        />
      </div>
    </div>
  )
}

function CardAction({
  icon: Icon,
  label,
  onClick,
  className,
}: {
  icon: typeof Plus
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-1.5 py-2.5 bg-card text-xs font-bold hover:bg-muted transition-colors',
        className,
      )}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  )
}
